import { Avatar, Box, Typography, IconButton, AppBar, useTheme, Popover, Select, MenuItem, Popper, ClickAwayListener, Card, Divider, Tooltip, Button } from "@mui/material"
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useEffect, useState } from "react"
import { observer } from 'mobx-react'

import { convertToCDNLink, ERPDashboardPageURL, formatDate, Icons, secondsToHMS } from "../../../utils/utilities"
import SimbaAIForStudents from '../../ai-mentor/SimbaAIForStudents'
import { useLayoutContext } from "../../../contexts/layout.context"
import { useThemeContext } from "../../../contexts/theme.context"
import { useUserStore } from "../../../store/user.store"
import aliveLogo from '../../../assets/alivelogo.png'
import simbaImg from '../../../assets/Habba-Logo.png'

const themeOptions = [
    { value: 'light', icon: Icons.default.LightMode, label: 'light' },
    { value: 'dark', icon: Icons.default.DarkMode, label: 'dark' },
    { value: 'system', icon: Icons.default.SystemTheme, label: 'system' },
]


const SessionEndCountdown = () => {
    const [timeLeft, setTimeLeft] = useState( null )

    const navigate = useNavigate()

    useEffect( () => {
        const calculateTimeLeft = () => {
            const endsAt = sessionStorage.getItem( 'impersonation_session_ends_at' )

            if ( !endsAt ) return null

            // Convert stored value to a date (handles both ISO strings and timestamps)
            const difference = new Date( endsAt ) - new Date()

            if ( difference <= 0 ) {
                sessionStorage.removeItem( 'impersonation_id_token' )
                sessionStorage.removeItem( 'is_impersonation' )
                sessionStorage.removeItem( 'impersonator_details' )
                sessionStorage.removeItem( 'impersonation_session_ends_at' )
                return window.location.href = '/admin/impersonation'
            }

            return difference
        }

        // Initial check
        setTimeLeft( calculateTimeLeft() )

        // Update every second
        const timer = setInterval( () => {
            const remaining = calculateTimeLeft()
            setTimeLeft( remaining )

            if ( remaining === 0 ) {
                clearInterval( timer )
                // Optional: Force a logout or refresh when timer hits zero
                // window.location.reload(); 
            }
        }, 1000 )

        return () => clearInterval( timer )
    }, [] )

    return (
        <Typography
            variant="caption"
            fontSize="10px"
            display="block"
            sx={{ fontWeight: 'bold', color: timeLeft < 60000 ? 'error.main' : 'inherit' }}
        >
            <Typography display={{ md: "inline", xs: "none" }} fontSize="inherit" fontWeight="inherit" color='inherit' component="span">Ends In:</Typography> {secondsToHMS( timeLeft / 1000 )}
        </Typography>
    )
}

const Navbar = observer( ( { openSideMenu, closeSideMenu } ) => {
    const [settingsAnchorEl, setSettingsAnchorEl] = useState( null )
    const [profile, setProfile] = useState( null )

    const searchParamsObject = useSearchParams( { simbaAIOpen: 'false' } )
    const setSearchParams = searchParamsObject[1]

    const UserStore = useUserStore()
    const isImpersonation = sessionStorage.getItem( "is_impersonation" ) === 'true'

    const { navHeight, navHeadText } = useLayoutContext()

    const { palette, border, shadows } = useTheme()
    const { setSelectedTheme, selectedTheme } = useThemeContext()

    const enterSimbaAI = () => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( 'simbaAIOpen', 'true' )
            newParams.set( 'simbaAIMode', 'practice' )
            return newParams
        }, { replace: true } )
    }

    const handleThemeChange = value => {
        setSelectedTheme( value )
        localStorage.setItem( 'preferedTheme', value )
        setSettingsAnchorEl( null )
    }
    useEffect( () => {
        setProfile( UserStore.getUser.user_profile )
    }, [UserStore] )

    return (
        <AppBar
            id="appbar"
            elevation={0}
            sx={{
                borderRadius: "0",
                background: "transparent",
                transition: "0.2s ease-out",
                position: "static",
                color: palette.nav.font,
                height: { sm: "50px", xs: "60px" },
                zIndex: "200"
            }}
        >

            <SimbaAIForStudents />


            <Box alignItems="center" justifyContent=" space-between" height={`${navHeight}px`} padding={{ sm: "0 20px 0 0", xs: "0 10px" }} display="flex" maxWidth="100%" id="app-bar" sx={{ transition: "0.2s ease-out", }}>
                <Box display="flex" gap="10px" alignItems="center">
                    <Box padding="2px" display={{ sm: "none", xs: "flex" }} alignItems="center" justifyContent="center" borderRadius="5px" sx={{ aspectRatio: "1 / 1" }} bgcolor="white"><Box component="img" width="35px" src={aliveLogo} alt="Logo" /></Box>
                    <Box display={{ sm: "block", xs: "none" }} component="img" src={simbaImg} width="20px" />
                    <Typography display={{ sm: "block", xs: "none" }} component='h1' fontSize="14px" color={palette.nav.font} >
                        Acharya LMS
                    </Typography>
                    <Typography display={{ sm: "block", xs: "none" }} component='span' fontSize="14px" color={palette.nav.font} >
                        |
                    </Typography>
                    <Typography color={palette.nav.font} component="span" fontSize={{ md: "14px", xs: "14px" }} >{navHeadText || `Welcome to digital classrooms`}</Typography>
                </Box>

                {<Box display="flex" alignItems="center" gap="10px">
                    {isImpersonation && <Box display="flex" flexWrap="wrap" justifyContent="center" bgcolor={palette.customThemeColor.light + "22"} minHeight="15px" gap={{ md: "10px", xs: "2px" }} alignItems="center" border={`1px solid ${palette.customThemeColor.main}`} padding={{ md: "2px 10px", xs: "5px 10px" }} borderRadius="20px">
                        {Icons.default.Impersonation}
                        <Box>
                            <Typography display={{ md: "block", xs: "none" }} fontSize="12px" variant="subtitle2">Impersonating</Typography>
                            <SessionEndCountdown />
                        </Box>
                    </Box>}
                    {UserStore.getUser.features_permissions['simba_ai'] && <Box onClick={enterSimbaAI} sx={{ cursor: "pointer", ":hover": { bgcolor: palette.customThemeColor.light + "dd" } }} display="flex" gap="10px" padding="5px 10px" alignItems="center" borderRadius="20px" boxShadow={shadows[1]} bgcolor={palette.customThemeColor.light + "99"} border={`2px solid ${palette.customThemeColor.main}`}>
                        <Box component="img" src={simbaImg} width="25px" borderRadius="50%" height="25px" border={`2px solid ${palette.common.white}`} />
                        <Box>
                            {/* <Typography width="fit-content" padding="1px 5px" fontSize="8px" sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, padding: "3px", borderRadius: "5px" }}>NEW</Typography> */}
                            <Typography fontSize="14px" fontWeight="700">Simba AI</Typography>
                        </Box>
                    </Box>}
                    <Box display={{ md: "block", xs: "none" }}>
                        <Typography fontSize="14px" textAlign="right" component="span" textTransform="capitalize">{formatDate( new Date(), { format: "LLL" } )}</Typography>
                    </Box>
                    {/* <IconButton onClick={( e ) => setSettingsAnchorEl( e.currentTarget )}>
                        {Icons.default.SettingsOutlined}
                    </IconButton> */}

                    <Avatar onClick={( e ) => setSettingsAnchorEl( e.currentTarget )} sx={{ height: "26px", width: "26px", cursor: "pointer", cursor: "pointer", fontSize: "14px !important" }}>
                        {( UserStore.getUser["name"] || UserStore.getUser["user_name"] || "" ).substring( 0, 1 )}
                    </Avatar>

                    <Popper sx={{ zIndex: 100 }} placement='bottom' open={Boolean( settingsAnchorEl )} anchorEl={settingsAnchorEl}>
                        <ClickAwayListener onClickAway={() => setSettingsAnchorEl( null )}>
                            <Card sx={{ marginTop: "10px", marginRight: "20px", border: border[1], borderRadius: "5px" }} elevation={1} >
                                <Box display="flex" alignItems="center" gap="5px" padding="5px">
                                    <Avatar onClick={( e ) => setSettingsAnchorEl( e.currentTarget )} src={convertToCDNLink( profile )} sx={{ height: "30px", width: "30px", borderRadius: "5px" }}></Avatar>
                                    <Box>
                                        <Typography display="block" lineHeight={1.3} fontSize="14px" variant='subtitle2' textTransform="capitalize">{UserStore.getUser["name"] || UserStore.getUser["employee_name"] || UserStore.getUser["student_name"] || ""} </Typography>
                                        <Typography display="block" lineHeight={1.3} fontSize="12px" color='textSecondary' variant='subtitle2' textTransform="capitalize">{UserStore.getUser["AUID"] || UserStore.getUser["empcode"] || UserStore.getUser["user_id"] || ""}</Typography>
                                    </Box>
                                </Box>

                                <Divider />

                                <Box padding="5px" justifyContent="space-between" display="flex" gap="20px" alignItems="center">
                                    <Box display="flex" gap="10px" alignItems="center">
                                        {Icons.default.PaletteOutlined}
                                        <Typography width="max-content" display="flex" fontSize="12px" variant="variant2">
                                            Theme
                                        </Typography>
                                    </Box>
                                    <Box boxShadow={`inset 0px 0px 3px 0px ${palette.primary.light + "44"}`} borderRadius="20px" bgcolor={palette.contentBg} padding="5px" display="flex" gap="2px" position="relative" alignItems="center">

                                        <Box position="absolute" bgcolor={palette.background.paper} borderRadius="50%" top="5px" bottom="5px" left={`${5 + ( themeOptions.findIndex( to => to.value === selectedTheme ) * 30 )}px`} height="calc(100% - 10px)" sx={{ aspectRatio: "1 / 1", transition: "all 300ms" }} />

                                        {themeOptions.map( theme => (
                                            <Tooltip disableInteractive key={theme.value} title={theme.label} >
                                                <IconButton onClick={() => handleThemeChange( theme.value )} size='small' >
                                                    {theme.icon}
                                                </IconButton>
                                            </Tooltip>
                                        ) )}
                                    </Box>
                                </Box>

                                <Divider />

                                <Box padding="5px">
                                    <Link to={ERPDashboardPageURL}  ><Button startIcon={Icons.default.Logout} color='error' size='small' fullWidth variant='contained' disableElevation >Back to ERP</Button></Link>
                                </Box>
                            </Card>
                        </ClickAwayListener>
                    </Popper>
                </Box>}
            </Box>
        </AppBar>
    )
} )

export default Navbar
